import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import { requestDataIssue, searchTeacherStudents } from "../services/TeacherAPIClient";
import { formatClassDisplayName } from "../utils/classDisplay";
import {
  assertSubmittedRequestResponse,
  getSupportRequestErrorMessage,
  getSupportRequestReference,
  hasPlaceholderTargetId,
  isDuplicatePendingResponse
} from "../utils/teacherWorkflowRequests";
import "./TeacherFixDuplicateChildResultsModal.css";

const STUDENT_SEARCH_MIN_LENGTH = 2;
const STUDENT_SEARCH_LIMIT = 25;
const STUDENT_SEARCH_DEBOUNCE_MS = 300;

const ISSUE_OPTIONS = [
  {
    value: "split-results",
    label: "Results are split",
    detail: "Some answers look like they are under another child profile"
  },
  {
    value: "missing-results",
    label: "Results are missing",
    detail: "Expected results are not visible"
  },
  {
    value: "tested-twice",
    label: "The child was tested twice",
    detail: "There may be two versions of the same screener"
  },
  {
    value: "review-needed",
    label: "I need this reviewed",
    detail: "Something else looks wrong"
  }
];

const mapIssueType = (value) => {
  if (value === "split-results") return "split_results";
  if (value === "missing-results") return "missing_results";
  if (value === "tested-twice") return "duplicate_screener";
  if (value === "wrong-child" || value === "investigate" || value === "review-needed") return "investigate_data_issue";
  return "other";
};

const ROUTE_OPTIONS = [
  {
    value: "duplicate-profile-review",
    label: "Review possible duplicate child profiles",
    detail: "Use this when answers are under another child profile"
  },
  {
    value: "missing-results-review",
    label: "Review missing results",
    detail: "Use this when captured results are not visible"
  },
  {
    value: "duplicate-screener-review",
    label: "Review duplicate screener versions",
    detail: "Use this when the child completed the same screener twice"
  },
  {
    value: "send-for-review",
    label: "Send for review",
    detail: "Use this when the issue needs a general results review"
  }
];

const ISSUE_ROUTE_MAP = {
  "split-results": "duplicate-profile-review",
  "missing-results": "send-for-review",
  "tested-twice": "duplicate-screener-review",
  "review-needed": "send-for-review"
};

const REVIEW_SUMMARY_ROWS = [
  ["Results are split", "Review possible duplicate child profiles"],
  ["Results are missing", "Send for review"],
  ["The child was tested twice", "Review duplicate screener versions"],
  ["Something else looks wrong", "Send for review"]
];

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseName = (value) => normaliseText(value).toLowerCase();

const normalisePrefill = (prefill) => ({
  studentName: normaliseText(prefill?.studentName || prefill?.student_name),
  className: normaliseText(prefill?.className || prefill?.class_name),
  issueType: normaliseText(
    prefill?.resultIssueType ||
      prefill?.result_issue_type ||
      prefill?.issueType ||
      prefill?.issue_type
  ),
  reviewRoute: normaliseText(
    prefill?.resultReviewRoute ||
      prefill?.result_review_route ||
      prefill?.reviewRoute ||
      prefill?.review_route
  ),
  assessmentName: normaliseText(prefill?.assessmentName || prefill?.assessment_name),
  note: normaliseText(prefill?.note)
});

const normaliseOptionValue = (value, options) => {
  const target = normaliseName(value);

  if (!target) {
    return "";
  }

  return (
    options.find(
      (option) =>
        normaliseName(option.value) === target ||
        normaliseName(option.label) === target ||
        normaliseName(option.detail) === target
    )?.value || ""
  );
};

const normaliseChild = (child, countryGrades) => ({
  ...child,
  id: child?.id || child?._id || child?.studentid || child?.studentId || child?.student_id,
  name: child?.name || child?.fullName || child?.displayName || "Unnamed child",
  classId: child?.classId || child?.classid || child?.class_id,
  className: child?.className || child?.class_name || "Class unavailable",
  classDisplayName: formatClassDisplayName(child, "Class unavailable", countryGrades),
  academicYear: child?.academicYear || child?.academic_year || "",
  grade: child?.grade || child?.gradeName || child?.grade_name || ""
});

const classMatchesPrefill = (child, className) => {
  const targetClassName = normaliseName(className);

  if (!targetClassName) {
    return true;
  }

  return (
    normaliseName(child.className) === targetClassName ||
    normaliseName(child.classDisplayName).includes(targetClassName)
  );
};

const findSingleChildMatch = (children, prefillContext) => {
  const targetChildName = normaliseName(prefillContext.studentName);

  if (!targetChildName) {
    return null;
  }

  const matches = children.filter(
    (child) =>
      normaliseName(child.name) === targetChildName &&
      classMatchesPrefill(child, prefillContext.className)
  );

  return matches.length === 1 ? matches[0] : null;
};

function TeacherFixDuplicateChildResultsModal({
  show,
  handleClose,
  prefill = null,
  schoolid = null,
  countryGrades = null,
  onViewResults = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const [stage, setStage] = useState("form");
  const [searchTerm, setSearchTerm] = useState("");
  const [matchingChildren, setMatchingChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedIssueType, setSelectedIssueType] = useState("");
  const [selectedReviewRoute, setSelectedReviewRoute] = useState("");
  const [note, setNote] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supportReference, setSupportReference] = useState("");
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);
  const prefillAppliedRef = useRef(false);

  const trimmedSearchTerm = searchTerm.trim();
  const selectedIssue = ISSUE_OPTIONS.find((option) => option.value === selectedIssueType);
  const selectedRoute = ROUTE_OPTIONS.find((option) => option.value === selectedReviewRoute);
  const recommendedRouteValue = ISSUE_ROUTE_MAP[selectedIssueType] || "send-for-review";
  const canContinue = Boolean(selectedChild?.id && selectedIssueType);
  const canSendReview = Boolean(selectedChild?.id && selectedIssueType && selectedReviewRoute);

  const resetForm = useCallback(() => {
    const prefilledIssue =
      normaliseOptionValue(prefillContext.issueType, ISSUE_OPTIONS) || ISSUE_OPTIONS[0].value;
    const prefilledRoute = normaliseOptionValue(prefillContext.reviewRoute, ROUTE_OPTIONS);

    setStage("form");
    setSearchTerm(prefillContext.studentName);
    setMatchingChildren([]);
    setSelectedChild(null);
    setSelectedIssueType(prefilledIssue);
    setSelectedReviewRoute(ISSUE_ROUTE_MAP[prefilledIssue] || prefilledRoute || "send-for-review");
    setNote(prefillContext.note);
    setIsSearching(false);
    setError("");
    setIsSubmitting(false);
    setSupportReference("");
    setIsDuplicatePending(false);
    prefillAppliedRef.current = false;
  }, [prefillContext]);

  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show, resetForm]);

  useEffect(() => {
    if (!show || trimmedSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      setMatchingChildren([]);
      setIsSearching(false);
      return undefined;
    }

    let isCurrentSearch = true;
    setIsSearching(true);
    setError("");

    const timeoutId = window.setTimeout(() => {
      searchTeacherStudents(trimmedSearchTerm, STUDENT_SEARCH_LIMIT)
        .then((children) => {
          if (!isCurrentSearch) {
            return;
          }

          const normalisedChildren = (Array.isArray(children) ? children : [])
            .map((child) => normaliseChild(child, countryGrades))
            .filter((child) => child.id);

          setMatchingChildren(normalisedChildren);
        })
        .catch((searchError) => {
          if (!isCurrentSearch) {
            return;
          }

          console.error("Error searching children for result issue review:", searchError);
          setMatchingChildren([]);
          setError("Child search could not be loaded. Please try again.");
        })
        .finally(() => {
          if (isCurrentSearch) {
            setIsSearching(false);
          }
        });
    }, STUDENT_SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrentSearch = false;
      window.clearTimeout(timeoutId);
    };
  }, [show, trimmedSearchTerm, countryGrades]);

  useEffect(() => {
    if (
      !show ||
      prefillAppliedRef.current ||
      selectedChild ||
      matchingChildren.length < 1
    ) {
      return;
    }

    prefillAppliedRef.current = true;
    const matchedChild = findSingleChildMatch(matchingChildren, prefillContext);

    if (matchedChild) {
      setSelectedChild(matchedChild);
    }
  }, [show, matchingChildren, selectedChild, prefillContext]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setMatchingChildren([]);
    setSelectedChild(null);
    setError("");
    prefillAppliedRef.current = true;
  };

  const handleIssueSelect = (issueValue) => {
    setSelectedIssueType(issueValue);
    setSelectedReviewRoute(ISSUE_ROUTE_MAP[issueValue] || "send-for-review");
  };

  const handleNext = () => {
    if (!canContinue) {
      return;
    }

    setSelectedReviewRoute((currentRoute) => currentRoute || recommendedRouteValue);
    setStage("review");
  };

  const handleSendReview = async () => {
    if (!canSendReview) {
      setError("Choose a child, issue, and review route before sending this request.");
      return;
    }

    if (!schoolid) {
      setError("School context is missing. Refresh and try again.");
      return;
    }

    const target = {
      type: "student",
      studentId: selectedChild?.id,
      classId: selectedChild?.classId
    };

    if (hasPlaceholderTargetId(target)) {
      setError("Choose a real child from search before sending this request.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const payload = {
      studentId: selectedChild?.id,
      classId: selectedChild?.classId,
      issueType: mapIssueType(selectedIssueType),
      reviewRoute: selectedReviewRoute,
      note: [
        selectedChild?.name,
        selectedChild?.className,
        selectedIssue?.label,
        selectedRoute?.label,
        normaliseText(note)
      ].filter(Boolean).join(" | ")
    };

    try {
      const response = await requestDataIssue(schoolid, payload);
      assertSubmittedRequestResponse(response);
      setSupportReference(getSupportRequestReference(response));
      setIsDuplicatePending(isDuplicatePendingResponse(response));
      setStage("success");
    } catch (submitError) {
      setError(getSupportRequestErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnotherIssue = () => {
    setStage("form");
    setSearchTerm("");
    setMatchingChildren([]);
    setSelectedChild(null);
    setSelectedIssueType(ISSUE_OPTIONS[0].value);
    setSelectedReviewRoute(ISSUE_ROUTE_MAP[ISSUE_OPTIONS[0].value]);
    setNote("");
    setIsSearching(false);
    setError("");
    setIsSubmitting(false);
    setSupportReference("");
    setIsDuplicatePending(false);
    prefillAppliedRef.current = true;
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: supportReference,
        message: "Result issue review request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderRadioCard = (name, option, selected, onSelect) => (
    <button
      key={option.value}
      type="button"
      className={`fix-child-results-choice${selected ? " selected" : ""}`}
      onClick={() => onSelect(option.value)}
      aria-label={`Select ${option.label}`}
      aria-pressed={selected}
    >
      <Form.Check
        type="radio"
        name={name}
        checked={selected}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
      />
      <span>
        <strong>{option.label}</strong>
        <span>{option.detail}</span>
      </span>
    </button>
  );

  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <div className="fix-child-results-search-state">
          <Spinner animation="border" size="sm" />
          <span>Searching children...</span>
        </div>
      );
    }

    if (trimmedSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      return <p className="fix-child-results-muted">Start typing a child name.</p>;
    }

    if (matchingChildren.length < 1) {
      return (
        <div className="fix-child-results-empty">
          <strong>No matching children found.</strong>
          <span>Try another child name or check the spelling.</span>
        </div>
      );
    }

    return matchingChildren.map((child) => {
      const isSelected = selectedChild?.id === child.id;

      return (
        <button
          key={`${child.classId || "class"}-${child.id}`}
          type="button"
          className={`fix-child-results-child${isSelected ? " selected" : ""}`}
          onClick={() => setSelectedChild(child)}
          aria-label={`Select ${child.name} from ${child.classDisplayName || child.className}`}
          aria-pressed={isSelected}
        >
          <Form.Check
            type="radio"
            checked={isSelected}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
          />
          <span>
            <strong>{child.name}</strong>
            <span>{child.classDisplayName || child.className}</span>
          </span>
        </button>
      );
    });
  };

  const renderSelectedChild = () => (
    <div className="fix-child-results-selected-child">
      <Form.Check
        type="radio"
        checked
        readOnly
        tabIndex={-1}
        aria-hidden="true"
      />
      <span>
        <strong>{selectedChild.name}</strong>
        <span>{selectedChild.classDisplayName || selectedChild.className}</span>
      </span>
      <Button
        type="button"
        variant="outline-secondary"
        size="sm"
        onClick={() => {
          setSelectedChild(null);
          setMatchingChildren([]);
          setError("");
          prefillAppliedRef.current = true;
        }}
      >
        Change
      </Button>
    </div>
  );

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Report Child Results Issue</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fix-child-results-intro">
          Find the child and choose what looks wrong.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group controlId="teacherFixChildResultsSearch">
          <Form.Label>Which child has the issue?</Form.Label>
          {!selectedChild && (
            <>
              <Form.Text as="div" className="fix-child-results-field-label">
                Search child
              </Form.Text>
              <Form.Control
                type="search"
                value={searchTerm}
                onChange={handleSearchChange}
                aria-label="Search child with result issue"
              />
            </>
          )}
        </Form.Group>

        <div className="fix-child-results-list" aria-label="Matching children">
          {selectedChild ? renderSelectedChild() : renderSearchResults()}
        </div>

        <div className="fix-child-results-section">
          <Form.Label as="div">What looks wrong?</Form.Label>
          <div className="fix-child-results-list">
            {ISSUE_OPTIONS.map((option) =>
              renderRadioCard(
                "teacherFixChildResultsIssue",
                option,
                selectedIssueType === option.value,
                handleIssueSelect
              )
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="fix-child-results-footer-note">
          {selectedChild ? `${selectedChild.name} selected.` : "Choose a child to continue."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleNext} disabled={!canContinue}>
          Next
        </Button>
      </Modal.Footer>
    </>
  );

  const renderReview = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Review Result Issue</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fix-child-results-intro">
          Choose the route that best matches the issue.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Label as="div">Recommended route</Form.Label>
        <Table bordered responsive size="sm" className="fix-child-results-route-table">
          <tbody>
            {REVIEW_SUMMARY_ROWS.map(([issue, route]) => (
              <tr key={issue}>
                <th scope="row">{issue}</th>
                <td>{route}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Form.Label as="div">What should happen?</Form.Label>
        <div className="fix-child-results-list">
          {ROUTE_OPTIONS.map((option) =>
            renderRadioCard(
              "teacherFixChildResultsRoute",
              option,
              selectedReviewRoute === option.value,
              setSelectedReviewRoute
            )
          )}
        </div>

        <Form.Group className="fix-child-results-section" controlId="teacherFixChildResultsNote">
          <Form.Label>Short note</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            aria-label="Short result issue note"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="fix-child-results-footer-note">
          {selectedRoute ? "Result issue ready." : "Choose a review route."}
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleSendReview}
          disabled={!canSendReview || isSubmitting}
        >
          {isSubmitting ? "Sending..." : "Send for review"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Done</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fix-child-results-intro">
          {isDuplicatePending
            ? `A result issue review is already in progress for ${selectedChild?.name || "the child"}.`
            : `The result issue review request has been sent for ${selectedChild?.name || "the child"}.`}
        </p>
        <Alert variant="success" className="fix-child-results-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>
              {isDuplicatePending
                ? "Your result issue is already being reviewed."
                : "Your result issue has been sent for review."}
            </strong>
            <span>
              No result data has been changed automatically.
            </span>
          </span>
        </Alert>
        <div className="fix-child-results-summary">
          <strong>What next?</strong>
          <span>
            {embedded ? "Done returns to SENSAI" : "Done returns to Help Centre home"}. Report another result issue repeats the action.
          </span>
        </div>
        <div className="fix-child-results-summary">
          <strong>Review details</strong>
          <span className="fix-child-results-row">
            <span>Child</span>
            <span>{selectedChild?.name}</span>
          </span>
          <span className="fix-child-results-row">
            <span>Issue</span>
            <span>{selectedIssueType === "review-needed" ? "I need this reviewed" : selectedIssue?.label}</span>
          </span>
          <span className="fix-child-results-row">
            <span>Route</span>
            <span>{selectedIssueType === "review-needed" ? "Send for review" : selectedRoute?.label}</span>
          </span>
          {note && (
            <span className="fix-child-results-row">
              <span>Note</span>
              <span>{note}</span>
            </span>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="fix-child-results-footer-note">Task complete.</span>
        <Button variant="outline-secondary" onClick={resetForAnotherIssue}>
          Report another issue
        </Button>
        {!embedded && onViewResults && (
          <Button
            variant="outline-secondary"
            onClick={() => onViewResults()}
          >
            View results
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  const renderStage = () => {
    if (stage === "review") {
      return renderReview();
    }

    if (stage === "success") {
      return renderSuccess();
    }

    return renderForm();
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      className="teacher-fix-duplicate-child-results-modal"
      backdrop="static"
    >
      {renderStage()}
    </Modal>
  );
}

export default TeacherFixDuplicateChildResultsModal;
